import { ApolloError } from "@apollo/client";
import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  Button,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
} from "@chakra-ui/react";
import { pdf } from "@react-pdf/renderer";
import { formatInTimeZone } from "date-fns-tz";
import { saveAs } from "file-saver";
import JSZip from "jszip";
import React, { useCallback } from "react";
import { RiDownloadLine } from "react-icons/ri";
import * as PDF from "src/components/Form/FormPdf";
import { useVisibilityContext } from "src/components/Providers/FormProvider";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import {
  DocumentStorageStatus,
  useDocumentStorage,
} from "src/hooks/useDocumentStorage";
import { useOrganization, useTimezoneName } from "src/hooks/useOrganization";
import { useSchoolAdmin } from "src/hooks/useSchoolAdmin";
import useUser from "src/hooks/useUser";
import {
  triggerDownload,
  triggerDownloadFromUrl,
} from "src/services/dataTransfer";
import * as FormTemplate from "src/services/formTemplate";
import { Question } from "src/services/formTemplate";
import * as Answer from "src/services/formTemplate/answer";
import { findSection, findSections } from "src/services/formTemplate/section";
import { Status } from "src/types/authData";
import * as AF from "src/types/formTemplate";
import { RemoteData } from "src/types/remoteData";
import { ExportRequest, SelectionType } from "../../dataServices/exports/types";
import { useExportManagementAPI } from "../../dataServices/exports/useExportManagementAPI";
import { CombinedData } from "./types";

interface ExportMenuProps {
  formData: RemoteData<ApolloError | Error, CombinedData>;
  enrollmentPeriodId: string;
}

export const ExportMenu: React.FC<ExportMenuProps> = ({
  formData,
  enrollmentPeriodId,
}) => {
  const [isExporting, setIsExporting] = React.useState<boolean>(false);
  const toast = useAvelaToast();
  const organization = useOrganization();
  const user = useUser();
  const api = useExportManagementAPI();
  const schoolAdminCheck = useSchoolAdmin();
  const { getDownloadUrl, getDocumentInfo } = useDocumentStorage();
  const visibilityContext = useVisibilityContext();
  const timezoneName = useTimezoneName();

  const handleExportCSV = useCallback(
    async () =>
      formData.do(async (app) => {
        const toastId = `form-csv-export-${Date.now()}`;
        const showError = (error: unknown) => {
          toast.update(toastId, {
            title: "Error exporting form",
            description: "Please try again later or contact our support team.",
            status: "error",
            duration: null,
            isClosable: true,
          });
          console.error(JSON.stringify(error));
        };

        try {
          const organizationId = organization.toNullable()?.id;
          if (!organizationId) {
            throw new Error("Organization not loaded");
          }
          if (
            user.status !== Status.OK &&
            user.status !== Status.NOT_VERIFIED
          ) {
            throw new Error("Can't find current user");
          }
          const login = user.data.loginType;
          if (login.type !== "email") {
            throw new Error("User does not have email");
          }
          const email = login.email;

          toast({
            description: "You'll receive a download and an email once ready.",
            duration: null,
            id: toastId,
            status: "loading",
            title: "Export in progress",
            isClosable: true,
          });

          // One row per chosen school.
          // Falls back to a single row with no school context if the form has no school choices.
          const ids =
            app.schoolsRank.length > 0
              ? app.schoolsRank.map((rank) => ({
                  formId: app.form.id,
                  schoolId: rank.school.id,
                  formSchoolRankId: rank.id,
                }))
              : [
                  {
                    formId: app.form.id,
                    schoolId: null,
                    formSchoolRankId: null,
                  },
                ];

          const questionIdsAndLabelsInOrder =
            Question.getQuestionIdsAndLabelsInOrder(
              FormTemplate.fromGQL(app.formTemplate).sections,
              schoolAdminCheck.isSchoolAdmin
                ? {
                    filterBy: "school",
                    schoolIdSet: new Set(schoolAdminCheck.allSchoolIds),
                  }
                : { filterBy: "none" }
            );
          const selectedQuestionIds = questionIdsAndLabelsInOrder.map(
            (q) => q.id
          );

          // Backend reads `enrollmentPeriod` from `searchParamsString` to
          // fetch tag groups; pass it so the Hasura query gets a real UUID.
          const exportSearchParams = new URLSearchParams({
            enrollmentPeriod: enrollmentPeriodId,
          });

          // Omit `selectedColumns` so the backend includes every non-question
          // column (its convention: `selectedColumns ?? null` => all columns).
          // We still send `selectedQuestionIds` because omitting that defaults
          // to no question columns server-side.
          const postData: ExportRequest = {
            exportConfig: {
              searchParamsString: exportSearchParams.toString(),
              selection: {
                selectionType: SelectionType.Inclusive,
                ids,
              },
              formTemplateId: app.formTemplate.id,
              selectedQuestionIds,
              questionKeysAsHeaders: false,
              exportSelectAnswerValues: false,
            },
            email,
          };

          const response = await api.createExport(organizationId, postData);
          if (!response?.pk) {
            throw new Error("Bulk export management API error");
          }
          const { pk } = response;

          const checkExportCompletion = async () => {
            try {
              const exportResp = await api.getOneExport(organizationId, pk);
              if (!exportResp || exportResp.status === "FAILED") {
                throw new Error("Export failed");
              }
              if (exportResp.status === "COMPLETED") {
                if (!exportResp.url) {
                  throw new Error("Export failed");
                }
                clearInterval(pollingInterval);
                triggerDownloadFromUrl(exportResp.url);
                toast.update(toastId, {
                  description:
                    "It has been automatically downloaded and sent to your email.",
                  status: "success",
                  title: "Export ready",
                  isClosable: true,
                });
              }
            } catch (error) {
              clearInterval(pollingInterval);
              showError(error);
            }
          };
          const pollingInterval = setInterval(
            async () => await checkExportCompletion(),
            5000
          );
        } catch (error) {
          showError(error);
        }
      }),
    [
      formData,
      toast,
      organization,
      user,
      schoolAdminCheck,
      enrollmentPeriodId,
      api,
    ]
  );

  const handleExportPDF = useCallback(async () => {
    formData.do(async (data: CombinedData) => {
      const {
        answers: {
          form_answer: formAnswers,
          grades_answer: gradesAnswers,
          form_address: addressAnswers,
          custom_question_answer: customQuestionAnswers,
          form_disclaimer: formDisclaimers,
        },
        custom_question_answer_bank_relationships:
          customQuestionAnswerBankRealtionships,
        schoolsRank,
        form: {
          person: {
            first_name: firstName,
            last_name: lastName,
            middle_name: middleName,
            birth_date: dateOfBirth,
          },
        },
        formTemplate: {
          name: formName,
          enrollment_period: { name: enrollmentPeriodName },
        },
      } = data;

      const pdfSections: PDF.SectionData[] = [];

      const formTemplate = FormTemplate.fromGQL(data.formTemplate);

      const questionIdsAndLabelsInOrder =
        Question.getQuestionIdsAndLabelsInOrder(
          formTemplate.sections,
          schoolAdminCheck.isSchoolAdmin
            ? {
                filterBy: "school",
                schoolIdSet: new Set(schoolAdminCheck.allSchoolIds),
              }
            : { filterBy: "none" }
        );
      const questionIdsInOrder = questionIdsAndLabelsInOrder.map((q) => q.id);

      const preRankingSection = findSection(
        formTemplate.sections,
        AF.PreRankingSectionType
      );
      const schoolRankingSection = findSection(
        formTemplate.sections,
        AF.SchoolRankingSectionType
      );
      const generalSections = findSections(
        formTemplate.sections,
        AF.GeneralSectionType
      );
      const disclaimerSection = findSection(
        formTemplate.sections,
        AF.DisclaimerSectionType
      );

      // The following are used in getCompleteApplicableQuestions
      const rankedSchoolIds = schoolsRank.map((rank) => rank.school.id);
      const previousFormSchoolIds = data.previousFormSchoolIds;
      const allQuestions = Question.getFormCompleteQuestions(formTemplate);
      const answers = Answer.getFormikInitialValues(
        allQuestions,
        formAnswers,
        gradesAnswers,
        addressAnswers,
        customQuestionAnswers,
        customQuestionAnswerBankRealtionships
      );

      const getAllQuestionsAndAnswers = (
        section: AF.PreRankingSection<AF.WithId> | AF.GeneralSection<AF.WithId>
      ): [AF.Question<AF.WithId>, Answer.FormikFieldValue][] => {
        // Use getCompleteApplicableQuestions to filter by visibility and archived status (same as form detail)
        const applicableQuestions = Question.getCompleteApplicableQuestions(
          section.questions,
          answers,
          {
            rankedSchoolIds,
            previousFormSchoolIds,
          },
          visibilityContext
        );

        return FormTemplate.Answer.getAllQuestionsAndAnswers(
          [...applicableQuestions],
          formAnswers,
          gradesAnswers,
          addressAnswers,
          customQuestionAnswers,
          customQuestionAnswerBankRealtionships
        );
      };

      /*
        pre-ranking section
      */
      if (preRankingSection) {
        const pdfSection: PDF.SectionData = {
          sectionName: preRankingSection.title,
          questionAnswers: [],
        };

        const questionsAndAnswers =
          getAllQuestionsAndAnswers(preRankingSection);
        const sortedQuestionsAndAnswers: [
          AF.Question<AF.WithId>,
          FormTemplate.Answer.Answer
        ][] = [];
        questionIdsInOrder.forEach((x) => {
          const questionAnswer = questionsAndAnswers.find(
            ([q, _]) => q.id === x
          );
          if (questionAnswer) sortedQuestionsAndAnswers.push(questionAnswer);
        });

        for (let idx = 0; idx < sortedQuestionsAndAnswers.length; idx++) {
          const item = sortedQuestionsAndAnswers[idx];
          if (!item) continue;
          const [question, answer] = item;

          const parsedAnswer = PDF.parseAnswer(question, answer, timezoneName);
          if (!parsedAnswer) continue;
          if (parsedAnswer.type === PDF.AnswerType.File) {
            const url = await getDownloadUrl(parsedAnswer.documentID);
            if (url.status === DocumentStorageStatus.OK) {
              pdfSection.questionAnswers.push({
                ...parsedAnswer,
                url: url.data,
              });
            }
          } else pdfSection.questionAnswers.push(parsedAnswer);
        }
        pdfSections.push(pdfSection);
      }

      /*
        school ranking section
      */
      if (schoolRankingSection && !schoolAdminCheck.isSchoolAdmin) {
        pdfSections.push({
          sectionName: schoolRankingSection.title,
          questionAnswers: [...schoolsRank]
            .sort((a, b) => a.rank - b.rank)
            .map((school) => ({
              type: PDF.AnswerType.String,
              question: schoolRankingSection.rankingEnabled
                ? (school.rank + 1).toString() + " " + school.school.name
                : school.school.name,
              answer: "",
            })),
        });
      }

      /*
        general sections
      */
      for (let idx = 0; idx < generalSections.length; idx++) {
        const section = generalSections[idx];
        if (!section) continue;
        const pdfSection: PDF.SectionData = {
          sectionName: section.title,
          questionAnswers: [],
        };

        const questionsAndAnswers = getAllQuestionsAndAnswers(section);
        const sortedQuestionsAndAnswers: [
          AF.Question<AF.WithId>,
          FormTemplate.Answer.Answer
        ][] = [];
        questionIdsInOrder.forEach((x) => {
          const questionAnswer = questionsAndAnswers.find(
            ([q, _]) => q.id === x
          );
          if (questionAnswer) sortedQuestionsAndAnswers.push(questionAnswer);
        });

        for (let jdx = 0; jdx < sortedQuestionsAndAnswers.length; jdx++) {
          const item = sortedQuestionsAndAnswers[jdx];
          if (!item) continue;
          const [question, answer] = item;

          const parsedAnswer = PDF.parseAnswer(question, answer, timezoneName);
          if (!parsedAnswer) continue;
          if (parsedAnswer.type === PDF.AnswerType.File) {
            const url = await getDownloadUrl(parsedAnswer.documentID);
            if (url.status === DocumentStorageStatus.OK) {
              pdfSection.questionAnswers.push({
                ...parsedAnswer,
                url: url.data,
              });
            }
          } else pdfSection.questionAnswers.push(parsedAnswer);
        }
        pdfSections.push(pdfSection);
      }

      /*
        disclaimer section
      */
      if (disclaimerSection && formDisclaimers.length > 0) {
        for (let idx = 0; idx < formDisclaimers.length; idx++) {
          const disclaimer = formDisclaimers[idx];
          if (!disclaimer) continue;
          pdfSections.push({
            sectionName: disclaimerSection.title,
            questionAnswers: [
              {
                type: PDF.AnswerType.String,
                question: disclaimer.signature,
                answer: formatInTimeZone(
                  disclaimer.signed_at,
                  timezoneName,
                  "MM/dd/yyyy"
                ),
              },
            ],
          });
        }
      }

      /*
        pdf payload
      */
      const payload: PDF.Data = {
        header: {
          fullName: middleName
            ? firstName + " " + middleName + " " + lastName
            : firstName + " " + lastName,
          dateOfBirth: dateOfBirth ?? "",
          organizationName: organization.hasData()
            ? organization.data.name
            : "",
          enrollmentPeriodName,
          formName,
        },
        sections: pdfSections,
      };
      const blob = await pdf(<PDF.FormPdf data={payload} />).toBlob();

      triggerDownload(
        new Blob([blob], { type: "application/pdf" }),
        formName + firstName + lastName + ".pdf"
      );
    });
  }, [
    formData,
    schoolAdminCheck,
    organization,
    visibilityContext,
    getDownloadUrl,
    timezoneName,
  ]);

  const handleExportFiles = useCallback(async () => {
    formData.do(async (app) => {
      const {
        answers,
        sections,
        form: {
          person: { first_name: firstName, last_name: lastName },
        },
        formTemplate: { name: formName },
      } = app;

      setIsExporting(true);
      const progressToast = toast({
        status: "info",
        title: "Export in progress",
        description: "Download will start shortly, please keep this page open",
        icon: <Spinner size="sm" />,
        duration: null,
      });

      const questions = sections.flatMap((section) => {
        if (section === undefined) return [];

        switch (section.type) {
          case AF.PreRankingSectionType:
          case AF.GeneralSectionType:
            return section.questions;
          default:
            return [];
        }
      });

      const documentIds = Answer.getAllDocumentIDs(
        questions,
        answers.form_answer
      );

      const zip = new JSZip();

      try {
        await Promise.all(
          documentIds.map(async (id) => {
            const url = await getDownloadUrl(id);
            if (url.status !== DocumentStorageStatus.OK) return;
            const documentInfo = await getDocumentInfo(id);

            return fetch(url.data, {
              method: "GET",
            })
              .then((response) => {
                return response.arrayBuffer();
              })
              .then((documentData) => {
                zip.file(`${id}-${documentInfo?.filename}`, documentData);
              });
          })
        );

        zip.generateAsync({ type: "blob" }).then((blob) => {
          toast.close(progressToast);
          setIsExporting(false);
          saveAs(blob, `${firstName}-${lastName}-${formName}.zip`);
          setTimeout(() => {
            toast({
              status: "success",
              title: "Export done",
              description: "Files downloaded successfully",
            });
          }, 500);
        });
      } catch (error) {
        console.error(error);
        toast.close(progressToast);
        setIsExporting(false);
        setTimeout(() => {
          toast({
            status: "error",
            title: "Oops! Something went wrong!",
            description: "Check your network and try again later",
          });
        }, 500);
      }
    });
  }, [formData, getDownloadUrl, getDocumentInfo, toast]);

  return (
    <Menu>
      <MenuButton
        as={Button}
        leftIcon={<Icon as={RiDownloadLine} />}
        rightIcon={<ChevronDownIcon />}
      >
        Export
      </MenuButton>
      <MenuList>
        <MenuItem onClick={handleExportCSV}>Form as .CSV</MenuItem>
        <MenuItem onClick={handleExportPDF}>Form as .PDF</MenuItem>
        <MenuItem onClick={handleExportFiles} isDisabled={isExporting}>
          Attached files
        </MenuItem>
      </MenuList>
    </Menu>
  );
};
